import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["name", "fullWidth", "color", "label", "bottomLabel", "error", "inputRef", "focused", "labelProps", "containerProps", "fieldProps", "options", "multiple", "value", "onChange", "filterName", "basicSelect"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, MenuItem, Typography } from '@mui/material';
import { longTextReplacer } from '@utils';
import { Button } from '@elements/button';
import { CheckboxField } from '@elements/form';
import { Tooltip } from '@elements/tooltip';
import { defaultTheme } from '@themes';
import { SelectMenuStyles, SelectPaperStyles, StyledFormControl, StyledFormHelperErrorText, StyledFormHelperText, StyledInputLabel, StyledSelect, StyleFormHelperWrapper } from './select-field.styles';
import { CloseIcon, SelectIcon } from './select-field.helpers';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/**
 * TextField component.
 *
 * @author Mikhail Siutsou
 * @function TextField
 * @category components
 * @return {JSX.Element} textfield component.
 */
var SelectField = function SelectField(_ref) {
  var name = _ref.name,
      fullWidth = _ref.fullWidth,
      color = _ref.color,
      label = _ref.label,
      bottomLabel = _ref.bottomLabel,
      error = _ref.error,
      inputRef = _ref.inputRef,
      focused = _ref.focused,
      labelProps = _ref.labelProps,
      containerProps = _ref.containerProps,
      fieldProps = _ref.fieldProps,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      multiple = _ref.multiple,
      value = _ref.value,
      onChange = _ref.onChange,
      filterName = _ref.filterName,
      basicSelect = _ref.basicSelect,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(value || []),
      _useState2 = _slicedToArray(_useState, 2),
      values = _useState2[0],
      setValues = _useState2[1];

  var _useState3 = useState((options === null || options === void 0 ? void 0 : options.filter(function (option) {
    if (value.find(function (item) {
      return item === option.value;
    })) {
      return option.label;
    }

    return '';
  }).map(function (item) {
    return item === null || item === void 0 ? void 0 : item.label;
  })) || []),
      _useState4 = _slicedToArray(_useState3, 2),
      labels = _useState4[0],
      setLabels = _useState4[1];

  var _useState5 = useState(value || []),
      _useState6 = _slicedToArray(_useState5, 2),
      appliedLabels = _useState6[0],
      setAppliedLabels = _useState6[1];

  var _useState7 = useState([]),
      _useState8 = _slicedToArray(_useState7, 2),
      appliedValues = _useState8[0],
      setAppliedValues = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      open = _useState10[0],
      setOpen = _useState10[1];

  var selectRef = useRef(null);
  var inputName = name && "".concat(name, "-select");

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var isError = !!error;
  var literalProps = {
    htmlFor: inputName,
    id: inputName,
    errorId: inputName && "".concat(inputName, "-error-text")
  };
  useEffect(function () {
    if (value.length === 0 || // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    value.includes(undefined)) {
      setValues([]);
      setAppliedValues([]);
      setAppliedLabels([]);
      setLabels([]);
    }
  }, [value]);
  /**
   * Toggle handler.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */

  var handleToggle = function handleToggle(event) {
    event.preventDefault();
    setOpen(function (prevOpen) {
      return !prevOpen;
    });
  };
  /**
   * Apply select changes.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleApply = function handleApply() {
    setAppliedValues(values);
    setAppliedLabels(labels);
    setOpen(false);

    if (onChange) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange(values);
    }
  };
  /**
   * Mouse down handler.
   *
   * @author Ihar Kazlouski
   * @param {Event | React.SyntheticEvent} event event.
   * @return {void}
   */


  var mouseDownHandler = function mouseDownHandler(event) {
    event.stopPropagation();
  };
  /**
   * Close handler.
   *
   * @author Ihar Kazlouski
   * @param {Event | React.SyntheticEvent} event event.
   * @return {void}
   */


  var handleClose = function handleClose(event) {
    event === null || event === void 0 ? void 0 : event.stopPropagation();
    setOpen(false);
  };
  /**
   * Handle change.
   *
   * @author Ihar Kazlouski
   * @param {SelectChangeEvent<unknown>} event event.
   * @return {void}
   */


  var handleChange = function handleChange(event) {
    if (event && event.target && event.target.value) {
      setValues(event.target.value);

      if (basicSelect && onChange) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange(event);
      }

      return;
    }
  };
  /**
   * Handle click.
   *
   * @author Ihar Kazlouski
   * @param {string | ReactElement} newLabel new label.
   * @return {void}
   */


  var handleClick = function handleClick(newLabel) {
    return function () {
      if (typeof newLabel === 'string') {
        if (labels.includes(newLabel)) {
          setLabels(function (prevState) {
            return prevState.filter(function (prevItem) {
              return prevItem !== newLabel;
            });
          });
          return;
        }

        setLabels(function (prevState) {
          return [].concat(_toConsumableArray(prevState), [newLabel]);
        });
      } else {
        var _newLabel$props;

        if (labels.includes(newLabel === null || newLabel === void 0 ? void 0 : (_newLabel$props = newLabel.props) === null || _newLabel$props === void 0 ? void 0 : _newLabel$props.label)) {
          setLabels(function (prevState) {
            return prevState.filter(function (prevItem) {
              var _newLabel$props2;

              return prevItem !== (newLabel === null || newLabel === void 0 ? void 0 : (_newLabel$props2 = newLabel.props) === null || _newLabel$props2 === void 0 ? void 0 : _newLabel$props2.label);
            });
          });
          return;
        }

        setLabels(function (prevState) {
          var _newLabel$props3;

          return [].concat(_toConsumableArray(prevState), [newLabel === null || newLabel === void 0 ? void 0 : (_newLabel$props3 = newLabel.props) === null || _newLabel$props3 === void 0 ? void 0 : _newLabel$props3.label]);
        });
      }
    };
  };
  /**
   * Handle delete values.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleDelete = function handleDelete() {
    setAppliedValues([]);
    setValues([]);
    setAppliedLabels([]);
    setLabels([]);

    if (onChange) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange([]);
    }
  };
  /**
   * Render selected values.
   *
   * @author Ihar Kazlouski
   * @return {ReactNode} render values
   */


  var renderSelectValues = function renderSelectValues() {
    return /*#__PURE__*/_jsxs(Box, {
      whiteSpace: "normal",
      display: "flex",
      children: [/*#__PURE__*/_jsxs(Typography, {
        variant: "body2",
        fontWeight: "500",
        color: basicSelect ? 'frenchGray' : 'white',
        children: [labels.length > 0 && basicSelect ? '' : filterName, basicSelect ? '' : ':']
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        ml: basicSelect ? 0 : 1.25,
        fontWeight: "500",
        children: basicSelect ? !(labels.join(', ').length > 15) ? labels.join(', ') : (labels === null || labels === void 0 ? void 0 : labels.length.toString()) + ' ' + t('common.select.selected') : !(appliedLabels.join(', ').length > 15) ? appliedLabels.join(', ') : (appliedLabels === null || appliedLabels === void 0 ? void 0 : appliedLabels.length.toString()) + ' ' + t('common.select.selected')
      })]
    });
  };

  return /*#__PURE__*/_jsx(StyledFormControl, {
    error: isError,
    fullWidth: fullWidth,
    focused: focused,
    children: /*#__PURE__*/_jsxs(Grid, _objectSpread(_objectSpread({
      container: true,
      rowSpacing: 0.5,
      columnSpacing: 2,
      alignItems: "center"
    }, containerProps), {}, {
      children: [label && /*#__PURE__*/_jsx(Grid, _objectSpread(_objectSpread({
        item: true,
        xs: 12
      }, labelProps), {}, {
        children: /*#__PURE__*/_jsx(StyledInputLabel, {
          shrink: true,
          htmlFor: literalProps.htmlFor,
          children: label
        })
      })), /*#__PURE__*/_jsx(Grid, _objectSpread(_objectSpread({
        item: true,
        flex: " 1"
      }, fieldProps), {}, {
        children: /*#__PURE__*/_jsxs(Box, {
          position: "relative",
          children: [/*#__PURE__*/_jsxs(StyledSelect, _objectSpread(_objectSpread({
            basicSelect: basicSelect,
            isApplied: !!appliedValues.length,
            error: isError,
            ref: selectRef,
            id: literalProps.id,
            "aria-describedby": inputName,
            name: name,
            fullWidth: fullWidth,
            onOpen: handleToggle,
            onClose: handleClose,
            open: open,
            color: color,
            value: values,
            multiple: multiple,
            onChange: handleChange,
            IconComponent: basicSelect ? SelectIcon : !appliedValues.length ? SelectIcon : function () {
              return /*#__PURE__*/_jsx(CloseIcon, {
                handleClick: handleDelete,
                mouseDown: mouseDownHandler
              });
            },
            options: options,
            MenuProps: {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right'
              },
              PaperProps: {
                style: SelectPaperStyles(defaultTheme)
              },
              MenuListProps: {
                sx: SelectMenuStyles(defaultTheme)
              }
            },
            displayEmpty: true,
            renderValue: function renderValue(selected) {
              return basicSelect ? renderSelectValues() : appliedValues.length > 0 ? renderSelectValues() : /*#__PURE__*/_jsx(Typography, {
                variant: "body2",
                fontWeight: "500",
                color: "comet",
                children: filterName
              });
            }
          }, restProps), {}, {
            children: [/*#__PURE__*/_jsx(MenuItem, {
              value: "",
              hidden: true
            }, 'none'), options.map(function (option) {
              var _option$label;

              return /*#__PURE__*/_jsxs(MenuItem, {
                value: option.value,
                disabled: option.disabled,
                hidden: option.hidden,
                onClick: handleClick(option.label),
                children: [multiple && typeof values !== 'string' && /*#__PURE__*/_jsx(CheckboxField, {
                  checked: values.includes(option.value),
                  iconColor: "frenchGray"
                }), /*#__PURE__*/_jsx(Tooltip, {
                  title: (option === null || option === void 0 ? void 0 : (_option$label = option.label) === null || _option$label === void 0 ? void 0 : _option$label.length) > 28 ? option === null || option === void 0 ? void 0 : option.label : '',
                  placement: "bottom-start",
                  children: /*#__PURE__*/_jsx(Typography, {
                    variant: "body2",
                    color: "comet",
                    lineHeight: 2.5,
                    children: longTextReplacer(option.label, 28)
                  })
                })]
              }, option.value);
            }), !basicSelect ? /*#__PURE__*/_jsx(Box, {
              pr: 1,
              pl: 1,
              pt: 1,
              children: /*#__PURE__*/_jsx(Button, {
                onClick: handleApply,
                variant: "contained",
                fullWidth: true,
                size: "small",
                children: t('common.select.applyFilter')
              })
            }) : null]
          })), isError && /*#__PURE__*/_jsx(StyleFormHelperWrapper, {
            children: /*#__PURE__*/_jsx(StyledFormHelperErrorText, {
              id: literalProps.errorId,
              children: error
            })
          }), bottomLabel && !isError ? /*#__PURE__*/_jsx(StyleFormHelperWrapper, {
            children: /*#__PURE__*/_jsx(StyledFormHelperText, {
              children: bottomLabel
            })
          }) : null]
        })
      }))]
    }))
  });
};

export { SelectField };